/**
 * Utilitários para gerenciamento de sessão no localStorage
 */

import i18n from '@/i18n'
import type { Database } from '@/lib/supabase'

type AccountUser = Database['public']['Tables']['account_users']['Row']
type Account = Database['public']['Tables']['accounts']['Row']

export interface AuthUser extends Partial<AccountUser> {
  id: string
  email: string
  name: string
  profile: string
  account_id: string
  account: Account
  auth_user_id?: string
  type?: 'account_user' | 'agency_user'
  agency_id?: string
}

export interface SupabaseSession {
  access_token: string
  refresh_token: string
  expires_in: number
  expires_at: number
}

export interface SessionData {
  user: AuthUser
  token: string
  agencyId: string
  expiresAt: number
  createdAt: number
  supabaseSession?: SupabaseSession
  availableAccounts?: Account[]
}

// Chaves para localStorage
const STORAGE_KEYS = {
  SESSION: 'rolt_user_session',
  LAST_ACTIVITY: 'rolt_last_activity'
} as const

// Configurações de sessão
const SESSION_CONFIG = {
  DURATION: 7 * 24 * 60 * 60 * 1000, // 7 dias em milliseconds
  ACTIVITY_TIMEOUT: 30 * 60 * 1000,   // 30 minutos de inatividade
  REFRESH_THRESHOLD: 24 * 60 * 60 * 1000 // Renovar se restam menos de 24h
} as const

/**
 * Salva a sessão do usuário no localStorage
 */
export const saveUserSession = (
  user: AuthUser,
  token: string,
  agencyId: string,
  supabaseSession?: SupabaseSession,
  availableAccounts?: Account[]
): void => {
  try {
    const now = Date.now()
    const sessionData: SessionData = {
      user,
      token,
      agencyId,
      expiresAt: now + SESSION_CONFIG.DURATION,
      createdAt: now,
      supabaseSession,
      availableAccounts
    }

    localStorage.setItem(STORAGE_KEYS.SESSION, JSON.stringify(sessionData))
    localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, now.toString())

    console.log('💾 [SessionStorage] Sessão salva com sucesso:', {
      user_id: user.id,
      user_name: user.name,
      user_email: user.email,
      agency_id: agencyId,
      expires_at: new Date(sessionData.expiresAt).toISOString(),
      duration_days: SESSION_CONFIG.DURATION / (24 * 60 * 60 * 1000),
      has_supabase_session: !!supabaseSession
    })
  } catch (error) {
    console.error('❌ [SessionStorage] Erro ao salvar sessão:', error)
    throw new Error(i18n.t('common:errors.sessionSaveFailed'))
  }
}

/**
 * Recupera a sessão do usuário do localStorage
 */
export const getUserSession = (): SessionData | null => {
  try {
    const sessionStr = localStorage.getItem(STORAGE_KEYS.SESSION)
    
    if (!sessionStr) {
      console.log('ℹ️ [SessionStorage] Nenhuma sessão encontrada no localStorage')
      return null
    }

    const sessionData: SessionData = JSON.parse(sessionStr)
    
    // Verificar se a sessão expirou
    const now = Date.now()
    if (now > sessionData.expiresAt) {
      console.log('⏰ [SessionStorage] Sessão expirada, removendo dados:', {
        expired_at: new Date(sessionData.expiresAt).toISOString(),
        current_time: new Date(now).toISOString()
      })
      clearUserSession()
      return null
    }

    // Atualizar última atividade
    updateLastActivity()

    console.log('✅ [SessionStorage] Sessão válida recuperada:', {
      user_id: sessionData.user.id,
      user_name: sessionData.user.name,
      agency_id: sessionData.agencyId,
      expires_at: new Date(sessionData.expiresAt).toISOString(),
      time_remaining: Math.round((sessionData.expiresAt - now) / (1000 * 60 * 60)) + ' horas'
    })

    return sessionData
  } catch (error) {
    console.error('❌ [SessionStorage] Erro ao recuperar sessão:', error)
    // Não limpar sessão em caso de erro de parsing - pode ser temporário
    console.log('⚠️ [SessionStorage] Mantendo dados para investigação')
    return null
  }
}

/**
 * Remove a sessão do usuário do localStorage
 */
export const clearUserSession = (): void => {
  try {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key)
    })
    console.log('🗑️ [SessionStorage] Sessão removida do localStorage')
  } catch (error) {
    console.error('❌ [SessionStorage] Erro ao limpar sessão:', error)
  }
}

/**
 * Atualiza o timestamp da última atividade
 */
export const updateLastActivity = (): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString())
  } catch (error) {
    console.error('❌ [SessionStorage] Erro ao atualizar última atividade:', error)
  }
}

/**
 * Verifica se a sessão precisa ser renovada
 */
export const shouldRefreshSession = (): boolean => {
  const sessionData = getUserSession()
  if (!sessionData) return false

  const timeRemaining = sessionData.expiresAt - Date.now()
  return timeRemaining < SESSION_CONFIG.REFRESH_THRESHOLD
}

/**
 * Verifica se o usuário está inativo há muito tempo
 */
export const isUserInactive = (): boolean => {
  try {
    const lastActivityStr = localStorage.getItem(STORAGE_KEYS.LAST_ACTIVITY)
    if (!lastActivityStr) return true

    const lastActivity = parseInt(lastActivityStr)
    const timeSinceActivity = Date.now() - lastActivity
    
    return timeSinceActivity > SESSION_CONFIG.ACTIVITY_TIMEOUT
  } catch (error) {
    console.error('❌ [SessionStorage] Erro ao verificar inatividade:', error)
    return true
  }
}

/**
 * Valida se a sessão pertence à agência atual
 * Agency users podem trocar entre contas da mesma agência
 */
export const validateSessionAgency = (currentAgencyId: string): boolean => {
  const sessionData = getUserSession()
  if (!sessionData) return false

  // Para agency_users, validar usando o agency_id da account atual
  // Eles podem trocar entre contas, desde que sejam da mesma agência
  if (sessionData.user?.type === 'agency_user') {
    const accountAgencyId = sessionData.user?.account?.agency_id

    const isValid = accountAgencyId === currentAgencyId

    console.log('🔍 [SessionStorage] Validando agência para agency_user:', {
      user_email: sessionData.user.email,
      account_agency_id: accountAgencyId,
      current_agency_id: currentAgencyId,
      is_valid: isValid
    })

    return isValid
  }

  const isValid = sessionData.agencyId === currentAgencyId

  if (!isValid) {
    console.log('❌ [SessionStorage] Sessão não pertence à agência atual:', {
      session_agency: sessionData.agencyId,
      current_agency: currentAgencyId
    })
    // Não limpar automaticamente - deixar para o usuário decidir
    console.log('⚠️ [SessionStorage] Sessão de agência diferente detectada, mas mantendo dados')
  }

  return isValid
}

/**
 * Obtém informações sobre a sessão atual
 */
export const getSessionInfo = (): {
  isActive: boolean
  user: AuthUser | null
  timeRemaining: number
  createdAt: Date | null
  expiresAt: Date | null
} => {
  const sessionData = getUserSession()
  
  if (!sessionData) {
    return {
      isActive: false,
      user: null,
      timeRemaining: 0,
      createdAt: null,
      expiresAt: null
    }
  }

  return {
    isActive: true,
    user: sessionData.user,
    timeRemaining: sessionData.expiresAt - Date.now(),
    createdAt: new Date(sessionData.createdAt),
    expiresAt: new Date(sessionData.expiresAt)
  }
}

/**
 * Estende a sessão atual (renova por mais 7 dias)
 */
export const extendUserSession = (): boolean => {
  try {
    const sessionData = getUserSession()
    if (!sessionData) return false

    const now = Date.now()
    const extendedSession: SessionData = {
      ...sessionData,
      expiresAt: now + SESSION_CONFIG.DURATION
    }

    localStorage.setItem(STORAGE_KEYS.SESSION, JSON.stringify(extendedSession))
    updateLastActivity()

    console.log('🔄 [SessionStorage] Sessão estendida com sucesso:', {
      new_expiry: new Date(extendedSession.expiresAt).toISOString()
    })

    return true
  } catch (error) {
    console.error('❌ [SessionStorage] Erro ao estender sessão:', error)
    return false
  }
}

// ====== Pipeline Storage ======

const PIPELINE_STORAGE_KEY = 'selected_pipeline';

/**
 * Salva o ID do pipeline selecionado no sessionStorage
 */
export const saveSelectedPipeline = (accountId: string, pipelineId: string): void => {
  try {
    const key = `${PIPELINE_STORAGE_KEY}_${accountId}`;
    sessionStorage.setItem(key, pipelineId);
  } catch (error) {
    console.error('Erro ao salvar pipeline selecionado:', error);
  }
};

/**
 * Recupera o ID do pipeline selecionado do sessionStorage
 */
export const getSelectedPipeline = (accountId: string): string | null => {
  try {
    const key = `${PIPELINE_STORAGE_KEY}_${accountId}`;
    return sessionStorage.getItem(key);
  } catch (error) {
    console.error('Erro ao recuperar pipeline selecionado:', error);
    return null;
  }
};

/**
 * Remove o ID do pipeline selecionado do sessionStorage
 */
export const clearSelectedPipeline = (accountId: string): void => {
  try {
    const key = `${PIPELINE_STORAGE_KEY}_${accountId}`;
    sessionStorage.removeItem(key);
  } catch (error) {
    console.error('Erro ao limpar pipeline selecionado:', error);
  }
};